import { useState, useRef, useEffect } from 'react'
import axios from 'axios'
import { baseURL } from '../../config'

const OTP_LENGTH = 6
const RESEND_COOLDOWN_SECONDS = 60

export default function ReauthModal({ isDarkMode, hasPassword = true, onVerified, onCancel }) {
	const [step, setStep] = useState(hasPassword ? 'password' : 'loading')
	const [password, setPassword] = useState('')
	const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''))
	const [reauthToken, setReauthToken] = useState(null)
	const [errorText, setErrorText] = useState('')
	const [loading, setLoading] = useState(false)
	const [resendCooldown, setResendCooldown] = useState(0)
	const inputRefs = useRef([])
	const passwordRef = useRef(null)

	useEffect(() => {
		if (hasPassword) {
			passwordRef.current?.focus()
		} else {
			requestEmailCode()
		}
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (resendCooldown <= 0) return
		const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000)
		return () => clearTimeout(timer)
	}, [resendCooldown])

	const handlePasswordSubmit = async e => {
		e.preventDefault()
		if (!password.trim()) return

		setLoading(true)
		setErrorText('')

		try {
			const res = await axios.post(`${baseURL}/api/user/reauth/verify-password`, {
				currentPassword: password,
			})

			if (res.data.valid) {
				onVerified({ currentPassword: password })
				return
			}

			if (res.data.requiresEmailReauth) {
				setPassword('')
				requestEmailCode()
				return
			}
		} catch (err) {
			setErrorText(err.response?.data?.error || 'Verification failed')
		} finally {
			setLoading(false)
		}
	}

	const requestEmailCode = async () => {
		setLoading(true)
		setErrorText('')

		try {
			const res = await axios.post(`${baseURL}/api/user/reauth/request-code`)

			if (res.data.pending && res.data.reauthToken) {
				setReauthToken(res.data.reauthToken)
				setStep('email-sent')
				setResendCooldown(RESEND_COOLDOWN_SECONDS)
				setTimeout(() => inputRefs.current[0]?.focus(), 100)
			} else {
				setStep(hasPassword ? 'password' : 'email-sent')
				setErrorText('Failed to send verification code')
			}
		} catch (err) {
			setStep(hasPassword ? 'password' : 'email-sent')
			setErrorText(err.response?.data?.error || 'Failed to send verification code')
		} finally {
			setLoading(false)
		}
	}

	const switchToEmailFlow = () => {
		setPassword('')
		setErrorText('')
		requestEmailCode()
	}

	const handleDigitChange = (index, value) => {
		if (!/^\d?$/.test(value)) return

		const updated = [...digits]
		updated[index] = value
		setDigits(updated)
		setErrorText('')

		if (value && index < OTP_LENGTH - 1) {
			inputRefs.current[index + 1]?.focus()
		}

		if (value && index === OTP_LENGTH - 1 && updated.every(d => d)) {
			submitCode(updated.join(''))
		}
	}

	const handleKeyDown = (index, e) => {
		if (e.key === 'Backspace' && !digits[index] && index > 0) {
			inputRefs.current[index - 1]?.focus()
		}
	}

	const handlePaste = e => {
		e.preventDefault()
		const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
		if (!pasted) return

		const updated = [...digits]
		for (let i = 0; i < pasted.length; i++) {
			updated[i] = pasted[i]
		}
		setDigits(updated)

		const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1)
		inputRefs.current[focusIndex]?.focus()

		if (updated.every(d => d)) {
			submitCode(updated.join(''))
		}
	}

	const submitCode = async code => {
		const otpCode = code || digits.join('')
		if (otpCode.length !== OTP_LENGTH) return

		setLoading(true)
		setErrorText('')

		try {
			const res = await axios.post(`${baseURL}/api/user/reauth/verify-code`, {
				reauthToken,
				reauthCode: otpCode,
			})

			if (res.data.valid) {
				onVerified({ reauthToken, reauthCode: otpCode })
			}
		} catch (err) {
			const msg = err.response?.data?.error || 'Verification failed'
			setErrorText(msg)
			setDigits(Array(OTP_LENGTH).fill(''))
			inputRefs.current[0]?.focus()
		} finally {
			setLoading(false)
		}
	}

	const cardBg = isDarkMode ? 'bg-gray-800' : 'bg-white'
	const textColor = isDarkMode ? 'text-gray-200' : 'text-gray-900'
	const subTextColor = isDarkMode ? 'text-gray-400' : 'text-gray-600'
	const borderColor = isDarkMode ? 'border-gray-600' : 'border-gray-300'
	const inputBg = isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'
	const iconBg = isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50'
	const iconColor = isDarkMode ? 'text-blue-400' : 'text-blue-600'

	return (
		<div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 pt-20 p-4">
			<div className={`${cardBg} rounded-lg w-full max-w-lg mx-4 overflow-hidden`}>
				<div className="p-6">
					{step === 'loading' && (
						<div className="flex flex-col items-center py-4">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3" />
							<p className={`text-sm ${subTextColor}`}>Sending verification code...</p>
						</div>
					)}

					{step === 'password' && (
						<>
							<div className="flex items-start gap-3 mb-4">
								<div className={`w-10 h-10 ${iconBg} rounded-full flex items-center justify-center shrink-0`}>
									<svg className={`w-5 h-5 ${iconColor}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
									</svg>
								</div>
								<div>
									<h3 className={`text-base font-semibold ${textColor}`}>
										Confirm your identity
									</h3>
									<p className={`text-sm ${subTextColor} mt-0.5`}>
										Enter your password to make this change.
									</p>
								</div>
							</div>

							{errorText && (
								<div className={`rounded-md p-2.5 mb-3 text-sm ${
									isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-600'
								}`}>
									{errorText}
								</div>
							)}

							<form onSubmit={handlePasswordSubmit}>
								<input
									ref={passwordRef}
									type="password"
									value={password}
									onChange={e => setPassword(e.target.value)}
									placeholder="Current password"
									className={`w-full rounded-md border ${borderColor} px-3 py-2 text-sm ${inputBg} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
									disabled={loading}
									autoComplete="current-password"
								/>

								<button
									type="submit"
									disabled={loading || !password.trim()}
									className="w-full mt-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
								>
									{loading ? 'Verifying...' : 'Confirm'}
								</button>
							</form>
						</>
					)}

					{step === 'email-sent' && (
						<>
							<div className="flex items-start gap-3 mb-4">
								<div className={`w-10 h-10 ${iconBg} rounded-full flex items-center justify-center shrink-0`}>
									<svg className={`w-5 h-5 ${iconColor}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
									</svg>
								</div>
								<div>
									<h3 className={`text-base font-semibold ${textColor}`}>
										Check your email
									</h3>
									<p className={`text-sm ${subTextColor} mt-0.5`}>
										Enter the 6-digit code we sent to your email.
									</p>
								</div>
							</div>

							{errorText && (
								<div className={`rounded-md p-2.5 mb-3 text-sm ${
									isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-600'
								}`}>
									{errorText}
								</div>
							)}

							<div className="flex gap-2 justify-center mb-4" onPaste={handlePaste}>
								{digits.map((digit, i) => (
									<input
										key={i}
										ref={el => (inputRefs.current[i] = el)}
										type="text"
										inputMode="numeric"
										maxLength={1}
										value={digit}
										onChange={e => handleDigitChange(i, e.target.value)}
										onKeyDown={e => handleKeyDown(i, e)}
										className={`w-11 h-11 text-center text-lg font-semibold border ${borderColor} rounded-md ${inputBg} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
										disabled={loading}
									/>
								))}
							</div>

							<button
								onClick={() => submitCode()}
								disabled={loading || digits.some(d => !d)}
								className="w-full py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
							>
								{loading ? 'Verifying...' : 'Verify'}
							</button>

							<div className="flex justify-center mt-2">
								<button
									onClick={requestEmailCode}
									disabled={resendCooldown > 0 || loading}
									className={`text-xs ${
										resendCooldown > 0
											? `${subTextColor} cursor-not-allowed`
											: 'text-blue-600 hover:text-blue-700'
									}`}
								>
									{resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
								</button>
							</div>
						</>
					)}
				</div>

				<div className={`px-5 py-3 flex items-center ${
					isDarkMode ? 'border-t border-gray-700' : 'bg-gray-50 border-t border-gray-100'
				}`}>
					{step === 'password' && (
						<button
							onClick={switchToEmailFlow}
							disabled={loading}
							className={`text-sm ${subTextColor} hover:${textColor}`}
						>
							Use email code instead
						</button>
					)}
					{step === 'email-sent' && hasPassword && (
						<button
							onClick={() => {
								setStep('password')
								setErrorText('')
								setDigits(Array(OTP_LENGTH).fill(''))
							}}
							className={`text-sm ${subTextColor} hover:${textColor}`}
						>
							Use password instead
						</button>
					)}
					<button
						onClick={onCancel}
						className={`text-sm ${subTextColor} hover:${textColor} ml-auto`}
					>
						Cancel
					</button>
				</div>
			</div>
		</div>
	)
}
